import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Upload, FileText, AlertCircle, CheckCircle } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { FieldMappingPrecheckModal } from '@/components/FieldMappingPrecheckModal';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface RealDataUploaderProps {
  onDataReady: (fileId: string, mapping: Record<string, string>, fileName: string) => void;
  maxFileSize?: number;
  acceptedFormats?: string[];
  customerId: string;
  moduleName: string;
}

export function RealDataUploader({ 
  onDataReady, 
  maxFileSize = 100 * 1024 * 1024, // 100MB (storage-baserad)
  acceptedFormats = ['.csv', '.txt'],
  customerId,
  moduleName
}: RealDataUploaderProps) {
  const [uploadedFileId, setUploadedFileId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showMappingModal, setShowMappingModal] = useState(false);
  const [csvContent, setCsvContent] = useState<string>('');
  const [fileName, setFileName] = useState<string>('');
  const { toast } = useToast();

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    if (acceptedFiles.length === 0) return;

    const file = acceptedFiles[0];
    
    if (file.size > maxFileSize) {
      setError(`Filen är för stor (${Math.round(file.size / 1024 / 1024)}MB). Max storlek: ${Math.round(maxFileSize / 1024 / 1024)}MB`);
      return;
    }

    try {
      setIsProcessing(true);
      setError(null);
      setUploadProgress(0);

      // Hämta användar-ID
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('Inte inloggad');
      }

      // Läs filinnehåll för preview i mapping modal
      const reader = new FileReader();
      const fileContent = await new Promise<string>((resolve, reject) => {
        reader.onload = (e) => resolve(e.target?.result as string);
        reader.onerror = reject;
        reader.readAsText(file, 'utf-8');
      });

      setCsvContent(fileContent);
      setFileName(file.name);

      // Sanera filnamn: ta bort ogiltiga tecken för Supabase Storage
      // Tillåt endast a-z, A-Z, 0-9, punkt, underscore och bindestreck
      const sanitizedFileName = file.name
        .replace(/[^a-zA-Z0-9._-]/g, '_')
        .replace(/_+/g, '_'); // Ersätt flera understreck i rad med ett

      // Ladda upp till storage
      const timestamp = Date.now();
      const filePath = `${user.id}/${timestamp}_${sanitizedFileName}`;
      
      setUploadProgress(30);
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('analysis-uploads')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) {
        throw new Error(`Upload misslyckades: ${uploadError.message}`);
      }

      setUploadProgress(70);
      setUploadedFileId(filePath);
      setUploadProgress(100);

      toast({
        title: 'Fil uppladdad',
        description: 'Konfigurera fältmappning för att fortsätta',
      });

      // Visa mappningsmodalen
      setShowMappingModal(true);
      
    } catch (err: any) {
      setError(`Fel vid uppladdning: ${err.message}`);
      console.error('Upload error:', err);
    } finally {
      setIsProcessing(false);
    }
  }, [maxFileSize, toast]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: acceptedFormats.reduce((acc, format) => {
      acc['text/csv'] = [format];
      return acc;
    }, {} as Record<string, string[]>),
    maxFiles: 1,
    disabled: isProcessing
  });

  const handleMappingConfirm = async (mapping: Record<string, string>, dialect: { delimiter: string; hasHeader: boolean }) => {
    try {
      if (!uploadedFileId) {
        throw new Error('Ingen fil uppladdad');
      }

      // Callback med fileId istället för parsed data
      onDataReady(uploadedFileId, mapping, fileName);
      setShowMappingModal(false);
      
      // Rensa state
      setCsvContent('');
    } catch (err: any) {
      setError(`Fel vid bekräftelse: ${err.message}`);
      console.error('Mapping confirm error:', err);
    }
  };

  const handleMappingClose = async () => {
    setShowMappingModal(false);
    
    // Om användaren avbryter, radera den uppladdade filen
    if (uploadedFileId) {
      try {
        await supabase.storage
          .from('analysis-uploads')
          .remove([uploadedFileId]);
      } catch (err) {
        console.error('Failed to delete uploaded file:', err);
      }
    }
    
    setUploadedFileId(null);
    setCsvContent('');
    setFileName('');
    setUploadProgress(0);
  };

  return (
    <div className="w-full">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            CSV-uppladdning med fältmappning
          </CardTitle>
          <CardDescription>
            Ladda upp CSV-fil och konfigurera automatisk fältmappning för skattekontoanalys
          </CardDescription>
        </CardHeader>
        
        <CardContent className="space-y-4">
          <div
            {...getRootProps()}
            className={`
              border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors
              ${isDragActive 
                ? 'border-primary bg-primary/10' 
                : 'border-muted-foreground/25 hover:border-primary/50'
              }
              ${isProcessing ? 'opacity-50 cursor-not-allowed' : ''}
            `}
          >
            <input {...getInputProps()} />
            
            {isProcessing ? (
              <div className="flex flex-col items-center gap-3">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                <p className="text-lg font-medium">Laddar upp fil...</p>
                {uploadProgress > 0 && (
                  <div className="w-64 bg-muted rounded-full h-2 mt-2">
                    <div 
                      className="bg-primary h-2 rounded-full transition-all duration-300"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                )}
              </div>
            ) : (
              <>
                <Upload className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
                <div>
                  <p className="text-lg font-medium">
                    {isDragActive ? 'Släpp filen här...' : 'Dra och släpp CSV-fil eller klicka för att välja'}
                  </p>
                  <p className="text-sm text-muted-foreground mt-2">
                    Stödda format: {acceptedFormats.join(', ')} (max {Math.round(maxFileSize / 1024 / 1024)}MB)
                  </p>
                </div>
              </>
            )}
          </div>

          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {uploadedFileId && !showMappingModal && (
            <div className="space-y-4">
              <Alert>
                <CheckCircle className="h-4 w-4 text-green-600" />
                <AlertDescription>
                  <strong>Fil uppladdad till backend!</strong><br />
                  Filnamn: {fileName}
                </AlertDescription>
              </Alert>

              <div className="flex gap-2">
                <Button 
                  onClick={async () => {
                    if (uploadedFileId) {
                      await supabase.storage
                        .from('analysis-uploads')
                        .remove([uploadedFileId]);
                    }
                    setUploadedFileId(null);
                    setFileName('');
                    setError(null);
                    setCsvContent('');
                  }}
                  variant="outline"
                >
                  Rensa
                </Button>
                <Button onClick={() => setShowMappingModal(true)}>
                  Konfigurera fältmappning
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <FieldMappingPrecheckModal
        isOpen={showMappingModal}
        onClose={handleMappingClose}
        onConfirm={handleMappingConfirm}
        csvContent={csvContent}
        customerId={customerId}
        moduleName={moduleName}
      />
    </div>
  );
}